import React from 'react'
import { Card, Heading, Text } from '../ui'

/**
 * Props interface for the PhilosophyGrid component
 */
export interface PhilosophyGridProps {
  /** Unique identifier for the section */
  id?: string
  /** CSS class name for custom styling */
  className?: string
}

/**
 * Philosophy data for the grid display
 */
const PHILOSOPHY_DATA = [
  {
    id: 'systems-thinking',
    title: 'Systems Thinking First',
    description: `I believe that great architecture comes from understanding
      the entire ecosystem—not just the code, but the people,
      processes, and business constraints that shape how systems
      evolve. Every technical decision I make considers the human
      element: the developers who will maintain it, the operators
      who will deploy it, and the users who depend on it.`,
  },
  {
    id: 'pragmatic-innovation',
    title: 'Pragmatic Innovation',
    description: `I'm passionate about emerging technologies like AI/ML and
      modern DevOps, but I've learned that the best solutions
      often combine cutting-edge tools with proven, battle-tested
      foundations. My approach is to innovate where it creates
      real value, while maintaining stability and reliability in
      the core systems that businesses depend on.`,
  },
  {
    id: 'team-leadership',
    title: 'Team-Centric Leadership',
    description: `The best architectures emerge from collaborative teams where
      every voice is heard. I focus on creating environments where
      engineers can do their best work—through clear technical
      standards, mentorship, and systems that make the right thing
      the easy thing. My role is to remove obstacles and amplify
      the team's collective intelligence.`,
  },
] as const

/**
 * Philosophy grid component showcasing technical philosophy and approach.
 *
 * Features:
 * - Card-based layout with philosophy statements
 * - Accessible structure with proper headings
 * - Responsive grid that adapts to different screen sizes
 * - Semantic HTML with ARIA labels
 */
export const PhilosophyGrid: React.FC<PhilosophyGridProps> = ({
  id,
  className = '',
}) => {
  return (
    <section
      className={`about-section ${className}`}
      aria-labelledby="philosophy-heading"
      id={id}
    >
      <Heading
        as="h2"
        size="xl"
        variant="default"
        className="section-title"
        id="philosophy-heading"
      >
        Technical Philosophy
      </Heading>

      <div className="philosophy-grid">
        {PHILOSOPHY_DATA.map(philosophy => (
          <Card
            key={philosophy.id}
            variant="glass"
            padding="lg"
            className="philosophy-card"
          >
            <Heading
              as="h3"
              size="lg"
              variant="default"
              id={`philosophy-${philosophy.id}`}
            >
              {philosophy.title}
            </Heading>
            <Text
              size="base"
              variant="secondary"
              aria-describedby={`philosophy-${philosophy.id}`}
            >
              {philosophy.description}
            </Text>
          </Card>
        ))}
      </div>
    </section>
  )
}
